/**
 * ===========================================================================
 *  OFFICERS — a real one-on-one fight, not a big grunt
 * ===========================================================================
 *
 * A Dynasty Warriors officer is a different game to the mob around him. He
 * blocks, he sidesteps, he punishes a swing into his guard, he has a
 * telegraphed guard-break, and he leaves a stagger window open after his own
 * combo — that window is the entire rhythm of the duel: bait, evade, punish.
 * Below 50 % and 20 % health he changes gear, and at 20 % he has a musou.
 *
 * Officer state lives in `entities.ext[id]` (a handful of units, so an object
 * is cheap and nothing here runs 600 times a frame). The record published on
 * `world.ai.officers` is the same object every frame — a HUD can hold a
 * reference and read `hp / maxHp / phase / engaged` without allocating.
 *
 *   world.ai.officers   -> [{ id, uid, spawnKey, name, title, team, kind, hp, maxHp,
 *                             phase, engaged, vulnerable, dist, alive }]
 *   world.ai.engaged    -> the officer currently duelling the player, or null
 */
import { ST, TEAM, KIND, FLAG } from '../core/entities.js';
import { clamp, clamp01 } from '../core/mathx.js';
import { ACT, ROLE, ORDERS, frac01, hash32 } from './store.js';
import { seek, flee } from './steering.js';
import { WP_WALLS } from './squad.js';
import { WALLS } from '../world/walls.js';

/**
 * `Math.hypot` is NOT a cheap intrinsic in V8: it is a variadic builtin that
 * boxes its arguments, and it measured at ~155 bytes of garbage per call
 * against ~0 for `Math.sqrt`. This module calls it once or more per unit per
 * fixed step, which is the whole of the AI's allocation rate and therefore the
 * whole of `aiCpuPeak` — the mean was never the problem, the scavenges landing
 * inside the unit loop were. Same arithmetic, no allocation.
 */
const hyp = (x, z) => Math.sqrt(x * x + z * z);


/** Officer sub-states. */
export const OF = {
  GUARD: 0, APPROACH: 1, STRAFE: 2, COMBO: 3, GAP: 4, STAGGER: 5, BLOCK: 6,
  COUNTER: 7, SIDESTEP: 8, GUARDBREAK: 9, MUSOU: 10, HURT: 11, ROAR: 12, DOWN: 13,
};
export const OF_NAME = ['guard', 'approach', 'strafe', 'combo', 'gap', 'stagger', 'block',
  'counter', 'sidestep', 'guardbreak', 'musou', 'hurt', 'roar', 'down'];

/* Named ranks for the anonymous officers the director keeps spawning. The
 * allied pool is the Majapahit founding generation — Ranggalawe, Nambi,
 * Sora, Kebo Anabrang, Wiraraja — so an ally who rides out is somebody the
 * setting can account for; the Kidul pool is wayang's sea and ogre nobility. */
const NAMES_KIDUL = ['Buta Rangin', 'Ki Lodra', 'Nyi Sanggara', 'Buta Werdi',
  'Rangga Wisha', 'Ki Jalasutra', 'Buta Kalantaka', 'Nyi Gadhung'];
const NAMES_WILWATIKTA = ['Ranggalawe', 'Gajah Sena', 'Nambi', 'Lembu Sora',
  'Kebo Anabrang', 'Arya Wiraraja', 'Jaran Bhaya', 'Mahisa Anggada'];
const TITLES = ['Vanguard Commander', 'Gate Captain', 'Cavalry Marshal', 'Left General',
  'Banner Lord', 'Ridge Warden', 'River Captain', 'Sword of the Kidul'];

const COMBO_CLIPS = ['attack_light_1', 'attack_light_2', 'attack_light_3', 'attack_light_4'];

/* ------------------------------------------------------------ hyper armour */
/**
 * What a light blow costs an officer's composure, and how fast he recovers it.
 *
 * This replaced a coin flip. `officerHit` used to send him to OF.HURT on a
 * ~50% chance PER LIGHT BLOW with no memory, which measured as a hard lock: a
 * player mashing the light attack killed a 900 hp Gate Captain in 6.3 s without
 * taking a single hit, because OF.STRAFE needs `rec.dur` (24-54 frames)
 * uninterrupted to reach its move-picking branch and his strafe runs never got
 * past 18. He spent 41.5% of that fight in hurt, 35% roaring, and started zero
 * attacks — while playing him properly (guard, punish the openings) took 13.8 s.
 * Mashing was both faster and free, so none of the authored moveset was
 * reachable. See the duel r1 verdict in docs/PROGRESS.jsonl.
 *
 * A pool instead of a coin means the flinch is EARNED and PREDICTABLE, which is
 * what the reference does: he eats a few, then breaks, and the break is a real
 * punish window rather than a 0.34 s stutter. He is still fully interruptible
 * by a launcher, so the moveset keeps a deliberate answer to him.
 *
 * Deliberately a pool on `rec` and not `entities.poise`: combat drains that one
 * itself in `applyImpulse` for its own knockback bookkeeping, and two systems
 * decrementing one counter for the same blow would double-count.
 */
const POISE_LIGHT = 15;     // a light blow. 60/15 = four before a phase-1 break
const POISE_HEAVY = 34;     // a charge or a guard-break: two
const POISE_REGEN = 26;     // per second, once he has been left alone
const POISE_CALM = 0.8;     // ...and how long "left alone" is

/** Build the ext record for a newly spawned officer. */
export function makeOfficer(w, id, opts = {}) {
  const e = w.entities;
  const uid = e.uid[id], key = e.spawnKey[id];
  const enemy = e.team[id] === TEAM.ENEMY;
  const pool = enemy ? NAMES_KIDUL : NAMES_WILWATIKTA;
  const rec = {
    id, uid, spawnKey: key,
    name: opts.name || pool[hash32(key) % pool.length],
    title: opts.title || TITLES[hash32(key + 7) % TITLES.length],
    team: e.team[id], kind: e.kind[id],
    hp: e.hp[id], maxHp: e.maxHp[id],
    phase: 1, engaged: false, vulnerable: false, dist: 999, alive: true,
    // --- private duel state
    of: OF.GUARD, ofT: 0, dur: 1, hit: 0, chain: 0, chainN: 2,
    cool: 0.6, gbCool: 4 + frac01(key, 3) * 4, musouCool: 12 + frac01(key, 5) * 8,
    // Hyper armour. Seeded from the entity's own per-kind poise (60 for an
    // officer, 90 for a general) so a general soaks more, exactly as elsewhere.
    poise: e.maxPoise[id] > 0 ? e.maxPoise[id] : 60,
    poiseMax: e.maxPoise[id] > 0 ? e.maxPoise[id] : 60,
    blockCool: 0, guard: 0, strafeDir: frac01(key, 7) < 0.5 ? -1 : 1,
    agg: 0.35 + frac01(key, 9) * 0.4, clip: 0, roared: 0, musouT: 0, lastHitT: -9,
  };
  e.ext[id] = rec;
  return rec;
}

/* ---------------------------------------------------------------- helpers */

const _ao = { blend: 0.1 };
function play(w, id, clip, blend = 0.1) { _ao.blend = blend; w.anim?.play?.(id, clip, _ao); }

/**
 * Enter an officer sub-state.
 *
 * SIDESTEP and MUSOU raise FLAG.INVULN; COMBO / GUARDBREAK / ROAR raise
 * FLAG.SUPERARMOR — and every one of them only ever cleared its own flag on its
 * own timeout. A blow that arrives mid-state jumps the officer straight to HURT
 * and used to strand the flag set for the rest of the battle: an enemy general
 * that `combat.damage()` refuses to touch, so the stage can never be won. Every
 * state that wants a flag re-raises it immediately AFTER this call, so dropping
 * them here is safe and closes the leak in one place.
 */
function setOf(w, rec, id, st, dur) {
  const e = w.entities;
  e.setFlag(id, FLAG.INVULN, false);
  if (st !== OF.COMBO && st !== OF.GAP && st !== OF.GUARDBREAK && st !== OF.MUSOU && st !== OF.ROAR) {
    e.setFlag(id, FLAG.SUPERARMOR, false);
  }
  rec.of = st; rec.ofT = 0; rec.dur = dur; rec.hit = 0;
}

function phaseOf(rec) {
  const f = rec.maxHp > 0 ? rec.hp / rec.maxHp : 1;
  return f <= 0.2 ? 3 : f <= 0.5 ? 2 : 1;
}

/* ============================================================== the fight */

/**
 * Full-detail update for one officer. Called every fixed step (there are only
 * ever a handful of them), and it owns this unit's desired velocity, facing
 * and entity state exactly like `grunt.act` does.
 */
export function officerAct(ctx, id, dt) {
  const w = ctx.w, e = w.entities, A = ctx.store;
  let rec = e.ext[id];
  if (!rec || rec.uid !== e.uid[id]) rec = makeOfficer(w, id);

  rec.hp = e.hp[id]; rec.maxHp = e.maxHp[id];

  // Composure comes back once he is left alone, so a player who breaks him and
  // then backs off does not get to keep a broken officer.
  if (rec.poise < rec.poiseMax && w.time.sim - rec.lastHitT > POISE_CALM) {
    rec.poise = Math.min(rec.poiseMax, rec.poise + POISE_REGEN * dt);
  }

  // ---- phase gate ------------------------------------------------------
  const ph = phaseOf(rec);
  if (ph > rec.phase) {
    rec.phase = ph;
    setOf(w, rec, id, OF.ROAR, 1.15);
    e.state[id] = ST.CELEBRATE;
    play(w, id, ph >= 3 ? 'celebrate' : 'taunt', 0.14);
    e.setFlag(id, FLAG.SUPERARMOR, true);
    e.setFlag(id, FLAG.HYPER, ph >= 3);
    w.vfx?.ring?.(e.x[id], e.y[id] + 1, e.z[id], { power: ph });
    w.camrig?.shake?.(0.25 * ph, 18, 0.3);
    w.bus.emit('officer:phase', id, ph);
    w.ui?.toast?.(`${rec.name} — ${ph >= 3 ? 'cornered!' : 'fights harder!'}`);
  }

  // ---- target ----------------------------------------------------------
  let tgt = A.tgt[id];
  if (tgt < 0 || !e.alive[tgt] || e.uid[tgt] !== A.tgtUid[id] || e.state[tgt] === ST.DEAD) {
    const pid = w.playerId;
    tgt = -1;
    if (e.team[id] === TEAM.ENEMY && pid >= 0 && e.alive[pid]) {
      const dx = e.x[pid] - e.x[id], dz = e.z[pid] - e.z[id];
      if (dx * dx + dz * dz < 26 * 26) tgt = pid;
    }
    if (tgt < 0) tgt = e.nearest(e.x[id], e.z[id], 22, e.team[id] === TEAM.PLAYER ? TEAM.ENEMY : TEAM.PLAYER, id);
    A.tgt[id] = tgt; A.tgtUid[id] = tgt >= 0 ? e.uid[tgt] : 0;
  }
  // A wall between him and his man ends the pursuit: he would otherwise
  // stand pressed to the masonry for as long as the target stayed alive.
  if (tgt >= 0 && WALLS.n && WALLS.blocked(e.x[id], e.z[id], e.x[tgt], e.z[tgt], 0)) {
    tgt = -1; A.tgt[id] = -1; A.tgtUid[id] = 0;
  }
  const alive = tgt >= 0;
  const tx = alive ? e.x[tgt] : e.x[id], tz = alive ? e.z[tgt] : e.z[id];
  const x = e.x[id], z = e.z[id];
  const dx = tx - x, dz = tz - z;
  const d = alive ? hyp(dx, dz) : 999;
  const face = Math.atan2(dx, dz);
  rec.dist = d;
  rec.engaged = alive && tgt === w.playerId && d < 16;
  rec.vulnerable = rec.of === OF.STAGGER || rec.of === OF.HURT;

  rec.ofT += dt;
  if (rec.cool > 0) rec.cool -= dt;
  if (rec.gbCool > 0) rec.gbCool -= dt;
  if (rec.musouCool > 0) rec.musouCool -= dt;
  if (rec.blockCool > 0) rec.blockCool -= dt;

  const sp = 3.0 + rec.phase * 0.35;
  const reach = 3.1;
  A.yawT[id] = alive ? face : e.yaw[id];

  switch (rec.of) {
    /* ------------------------------------------------------- idle guard */
    case OF.GUARD: {
      A.dvx[id] = 0; A.dvz[id] = 0;
      e.state[id] = ST.RUN;
      if (!alive) {
        // An officer with nothing to fight used to stand exactly still for the
        // rest of the battle, which also froze the retinue that was supposed to
        // be escorting him (ORDERS.ESCORT anchors the squad ON him). He walks
        // his squad's objective instead, and the squad follows.
        const sq = A.squad[id], S = ctx.squads;
        if (sq >= 0 && S.alive[sq] && (S.order[sq] === ORDERS.ESCORT || S.order[sq] === ORDERS.ADVANCE)) {
          // inside walls he walks the squad's next gate, not the goal beyond it
          const walled = S.wp[sq] === WP_WALLS;
          const gx = walled ? S.wpx[sq] : S.gx[sq], gz = walled ? S.wpz[sq] : S.gz[sq];
          const rem = seek(A, id, x, z, gx, gz, Math.max(2.2, S.speed[sq]), 2.0);
          if (rem > 1.0) A.yawT[id] = Math.atan2(gx - x, gz - z);
          else { A.dvx[id] = 0; A.dvz[id] = 0; A.yawT[id] = Math.atan2(S.fx[sq], S.fz[sq]); }
        } else if (sq >= 0 && S.alive[sq] && S.order[sq] === ORDERS.ROUT) {
          flee(A, id, x, z, S.ax[sq] - S.fx[sq] * 20, S.az[sq] - S.fz[sq] * 20, 4.6);
          A.yawT[id] = Math.atan2(A.dvx[id], A.dvz[id]);
        }
        break;
      }
      if (d > 4.2) { setOf(w, rec, id, OF.APPROACH, 6); break; }
      if (rec.ofT > 0.35) setOf(w, rec, id, OF.STRAFE, 0.7 + frac01(rec.spawnKey, w.time.tick & 255) * 1.1);
      break;
    }

    /* --------------------------------------------------------- approach */
    case OF.APPROACH: {
      if (!alive) { setOf(w, rec, id, OF.GUARD, 1); break; }
      seek(A, id, x, z, tx, tz, sp * 1.55, 2.5);
      e.state[id] = ST.RUN;
      if (d < 3.6) setOf(w, rec, id, OF.STRAFE, 0.4 + frac01(rec.spawnKey, 11) * 0.5);
      break;
    }

    /* ----------------------------------------------------------- strafe */
    case OF.STRAFE: {
      if (!alive) { setOf(w, rec, id, OF.GUARD, 1); break; }
      // circle at duelling distance, sliding in and out
      const want = 2.9 + Math.sin(w.time.sim * 0.9 + rec.spawnKey) * 0.5;
      const px = Math.cos(face) * rec.strafeDir, pz = -Math.sin(face) * rec.strafeDir;
      const gx = tx - Math.sin(face) * want + px * 1.6;
      const gz = tz - Math.cos(face) * want + pz * 1.6;
      seek(A, id, x, z, gx, gz, sp, 1.2);
      e.state[id] = ST.RUN;
      if (rec.ofT < rec.dur) break;

      // ---- pick the next move -----------------------------------------
      const r = frac01(rec.spawnKey, w.time.tick & 4095);
      const inRange = d < reach + 0.7;
      if (rec.phase >= 3 && rec.musouCool <= 0 && d < 6.5) {
        setOf(w, rec, id, OF.MUSOU, 2.3);
        rec.musouCool = 16 + frac01(rec.spawnKey, 13) * 8;
        e.setFlag(id, FLAG.SUPERARMOR | FLAG.INVULN, true);
        e.state[id] = ST.CHARGE;
        play(w, id, 'attack_charge', 0.1);
        w.camrig?.shake?.(0.5, 16, 0.5);
        w.ui?.toast?.(`${rec.name} unleashes his musou!`);
      } else if (inRange && rec.gbCool <= 0 && r < 0.34) {
        setOf(w, rec, id, OF.GUARDBREAK, 1.15);
        rec.gbCool = 6 + frac01(rec.spawnKey, 17) * 5;
        e.state[id] = ST.CHARGE;
        e.setFlag(id, FLAG.SUPERARMOR, true);
        play(w, id, 'attack_charge', 0.12);
      } else if (inRange && rec.cool <= 0 && r < 0.34 + rec.agg * 0.42) {
        rec.chain = 0;
        rec.chainN = rec.phase >= 2 ? 3 + (hash32(rec.spawnKey + w.time.tick) & 1) : 2 + (hash32(rec.spawnKey) & 1);
        startComboHit(ctx, rec, id);
      } else if (r < 0.62 && d < 5 && rec.blockCool <= 0) {
        setOf(w, rec, id, OF.BLOCK, 0.7 + frac01(rec.spawnKey, 19) * 0.9);
        e.state[id] = ST.BLOCK;
        rec.guard = 1;
      } else if (r < 0.76 && d < 6) {
        setOf(w, rec, id, OF.SIDESTEP, 0.62);
        rec.strafeDir = -rec.strafeDir;
        e.state[id] = ST.DODGE;
        e.setFlag(id, FLAG.INVULN, true);
        play(w, id, 'dodge_roll', 0.08);
      } else {
        rec.dur = 0.5 + frac01(rec.spawnKey, w.time.tick & 511) * 1.0;
        rec.ofT = 0;
        if (r > 0.9) rec.strafeDir = -rec.strafeDir;
        if (d > 4.6) setOf(w, rec, id, OF.APPROACH, 6);
      }
      break;
    }

    /* ------------------------------------------------------------ combo */
    case OF.COMBO: {
      const t = rec.ofT / rec.dur;
      if (alive && d > reach) seek(A, id, x, z, tx, tz, 2.4, 1.0);
      else { A.dvx[id] = 0; A.dvz[id] = 0; }
      if (!rec.hit && t >= 0.40) {
        rec.hit = 1;
        e.state[id] = ST.ATTACK;
        // Committed. From here the swing lands whatever the player does to him.
        //
        // `startComboHit` arms this at phase 2 and above, which left phase 1 --
        // where a fight spends its first 450 hp -- with a combo that a jab could
        // cancel. Measured after the poise pool went in: a mashing player took 2
        // damage over the whole kill, because the officer kept STARTING attacks
        // (8.3% combo occupancy, real 20-frame tells) and never finished one.
        // Poise decides whether he can begin a swing; this decides whether he
        // completes it, and a general you can jab out of his own arc mid-arc is
        // what made mashing free.
        //
        // The WINDUP stays vulnerable at phase 1 on purpose: reading the tell
        // and interrupting it is the skill the fight asks for, so it keeps
        // paying. What stops paying is swinging blind.
        e.setFlag(id, FLAG.SUPERARMOR, true);
        officerStrike(ctx, id, rec, tgt, reach + 0.5, 26 + rec.phase * 8, rec.chain + 1 >= rec.chainN ? 1 : 0);
      }
      if (rec.ofT >= rec.dur) {
        rec.chain++;
        if (rec.chain < rec.chainN && alive && d < reach + 2.2) {
          setOf(w, rec, id, OF.GAP, 0.10 + frac01(rec.spawnKey, rec.chain) * 0.08);
          e.state[id] = ST.RECOVER;
        } else {
          // THE window: the officer is open here and the player is meant to
          // recognise it and punish it.
          setOf(w, rec, id, OF.STAGGER, rec.phase >= 3 ? 0.34 : rec.phase === 2 ? 0.46 : 0.62);
          e.state[id] = ST.STUN;
          e.setFlag(id, FLAG.SUPERARMOR, false);
          play(w, id, 'hit_light', 0.16);
          rec.cool = 0.8 + frac01(rec.spawnKey, 23) * 0.9 - rec.phase * 0.15;
        }
      }
      break;
    }
    case OF.GAP: {
      A.dvx[id] = 0; A.dvz[id] = 0;
      if (rec.ofT >= rec.dur) startComboHit(ctx, rec, id);
      break;
    }

    /* ---------------------------------------------------------- stagger */
    case OF.STAGGER: {
      A.dvx[id] = 0; A.dvz[id] = 0;
      if (rec.ofT >= rec.dur) { setOf(w, rec, id, OF.STRAFE, 0.35); e.state[id] = ST.RUN; }
      break;
    }

    /* ------------------------------------------------------------ block */
    case OF.BLOCK: {
      A.dvx[id] = 0; A.dvz[id] = 0;
      e.state[id] = ST.BLOCK;
      // shuffle backwards a touch when he is being crowded
      if (alive && d < 1.9) seek(A, id, x, z, x - dx, z - dz, 1.2, 1);
      if (rec.ofT >= rec.dur) {
        rec.guard = 0;
        rec.blockCool = 1.2 + frac01(rec.spawnKey, 29) * 1.4;
        setOf(w, rec, id, OF.STRAFE, 0.4);
        e.state[id] = ST.RUN;
        w.anim?.stop?.(id);            // `block` loops; tell the animator to let go
      }
      break;
    }

    /* ---------------------------------------------------------- counter */
    case OF.COUNTER: {
      A.dvx[id] = 0; A.dvz[id] = 0;
      if (!rec.hit && rec.ofT >= rec.dur * 0.34) {
        rec.hit = 1;
        e.state[id] = ST.ATTACK;
        officerStrike(ctx, id, rec, tgt, reach + 0.8, 42 + rec.phase * 10, 1);
        w.camrig?.shake?.(0.3, 22, 0.2);
      }
      if (rec.ofT >= rec.dur) { setOf(w, rec, id, OF.STRAFE, 0.4); e.state[id] = ST.RUN; }
      break;
    }

    /* --------------------------------------------------------- sidestep */
    case OF.SIDESTEP: {
      const px = Math.cos(face) * rec.strafeDir, pz = -Math.sin(face) * rec.strafeDir;
      A.dvx[id] = px * 6.2; A.dvz[id] = pz * 6.2;
      if (rec.ofT >= rec.dur) {
        e.setFlag(id, FLAG.INVULN, false);
        setOf(w, rec, id, OF.STRAFE, 0.3);
        e.state[id] = ST.RUN;
      }
      break;
    }

    /* ------------------------------------------------------ guard break */
    case OF.GUARDBREAK: {
      // long telegraph, then a blow that goes through a guard
      if (rec.ofT < rec.dur * 0.55) { A.dvx[id] = 0; A.dvz[id] = 0; }
      else if (alive) seek(A, id, x, z, tx, tz, 5.5, 1.2);
      if (!rec.hit && rec.ofT >= rec.dur * 0.62) {
        rec.hit = 1;
        e.state[id] = ST.ATTACK;
        officerStrike(ctx, id, rec, tgt, reach + 1.3, 58 + rec.phase * 12, 2);
        w.camrig?.shake?.(0.45, 20, 0.3);
        w.vfx?.ring?.(e.x[id], e.y[id] + 0.3, e.z[id], { power: 1.4 });
      }
      if (rec.ofT >= rec.dur) {
        e.setFlag(id, FLAG.SUPERARMOR, false);
        setOf(w, rec, id, OF.STAGGER, 0.55);
        e.state[id] = ST.STUN;
        play(w, id, 'hit_light', 0.15);
      }
      break;
    }

    /* ------------------------------------------------------------ musou */
    case OF.MUSOU: {
      if (alive && d > 2.6) seek(A, id, x, z, tx, tz, 3.4, 1.4); else { A.dvx[id] = 0; A.dvz[id] = 0; }
      e.state[id] = ST.CHARGE;
      rec.musouT -= dt;
      if (rec.musouT <= 0) {
        rec.musouT = 0.28;
        musouSweep(ctx, id, rec);
      }
      if (rec.ofT >= rec.dur) {
        e.setFlag(id, FLAG.SUPERARMOR | FLAG.INVULN, false);
        setOf(w, rec, id, OF.STAGGER, 1.0);           // the classic punish window
        e.state[id] = ST.STUN;
        play(w, id, 'hit_heavy', 0.18);
      }
      break;
    }

    /* ------------------------------------------------------------- hurt */
    case OF.HURT: {
      A.dvx[id] = 0; A.dvz[id] = 0;
      if (rec.ofT >= rec.dur) { setOf(w, rec, id, OF.STRAFE, 0.3); e.state[id] = ST.RUN; }
      break;
    }

    /* ------------------------------------------------------------- roar */
    case OF.ROAR: {
      A.dvx[id] = 0; A.dvz[id] = 0;
      if (rec.ofT >= rec.dur) {
        e.setFlag(id, FLAG.SUPERARMOR, false);
        setOf(w, rec, id, OF.STRAFE, 0.3);
        e.state[id] = ST.RUN;
      }
      break;
    }

    default: { A.dvx[id] = 0; A.dvz[id] = 0; break; }
  }
  return rec;
}

function startComboHit(ctx, rec, id) {
  const w = ctx.w, e = w.entities;
  const heavy = rec.chain + 1 >= rec.chainN && rec.phase >= 2;
  const clip = heavy ? 'attack_heavy' : COMBO_CLIPS[(rec.clip = (rec.clip + 1) & 3)];
  const dur = (w.anim?.duration?.(clip) || 0.6) * 0.88;
  setOf(w, rec, id, OF.COMBO, dur);
  e.state[id] = ST.WINDUP;
  if (rec.phase >= 2) e.setFlag(id, FLAG.SUPERARMOR, true);
  play(w, id, clip, 0.08);
}

/** One officer blow. `kind` 0 normal, 1 heavy/finisher, 2 guard-break. */
function officerStrike(ctx, id, rec, tgt, reach, amount, kind) {
  const w = ctx.w, e = w.entities;
  if (tgt < 0 || !e.alive[tgt] || e.state[tgt] === ST.DEAD) return;
  const dx = e.x[tgt] - e.x[id], dz = e.z[tgt] - e.z[id];
  const d = hyp(dx, dz);
  if (d > reach) return;
  const fx = Math.sin(e.yaw[id]), fz = Math.cos(e.yaw[id]);
  if ((fx * dx + fz * dz) / (d || 1) < 0.34) return;
  ctx.hurt(tgt, amount * ctx.damageMul, id, dx / (d || 1), dz / (d || 1), kind);
}

/** Officer musou: sweeps everything around him. */
function musouSweep(ctx, id, rec) {
  const w = ctx.w, e = w.entities;
  const n = e.around(e.x[id], e.z[id], 4.2, ctx.q, ctx.q.length);
  for (let i = 0; i < n; i++) {
    const o = ctx.q[i];
    if (o === id || !e.alive[o] || e.state[o] === ST.DEAD) continue;
    if (e.team[o] === e.team[id]) continue;
    const dx = e.x[o] - e.x[id], dz = e.z[o] - e.z[id];
    const d = hyp(dx, dz);
    if (d > 4.2) continue;
    ctx.hurt(o, (18 + rec.phase * 6) * ctx.damageMul, id, dx / (d || 1), dz / (d || 1), 2);
  }
  w.vfx?.ring?.(e.x[id], e.y[id] + 0.4, e.z[id], { power: 1.2 });
  w.camrig?.shake?.(0.22, 26, 0.18);
}

/* ============================================================== reactions */

/**
 * An officer took a hit. Returns true when the AI layer should NOT run the
 * normal grunt flinch (i.e. the officer absorbed, guarded or countered it).
 */
export function officerHit(ctx, id, amount, srcId, dirX, dirZ, heavy) {
  const w = ctx.w, e = w.entities;
  const rec = e.ext[id];
  if (!rec || rec.uid !== e.uid[id]) return false;
  rec.hp = e.hp[id];
  rec.lastHitT = w.time.sim;

  // ---- guarded ---------------------------------------------------------
  const inFront = (() => {
    if (srcId < 0 || !e.alive[srcId]) return true;
    const dx = e.x[srcId] - e.x[id], dz = e.z[srcId] - e.z[id];
    const d = hyp(dx, dz) || 1;
    return (Math.sin(e.yaw[id]) * dx + Math.cos(e.yaw[id]) * dz) / d > 0.15;
  })();

  if (rec.of === OF.BLOCK && inFront && heavy < 2) {
    // Guard holds: most of the damage is refunded (the combat module does not
    // expose a guard hook yet — see docs/REQUESTS.md) and the officer punishes.
    e.hp[id] = Math.min(e.maxHp[id], e.hp[id] + amount * 0.78);
    rec.hp = e.hp[id];
    play(w, id, 'block_impact', 0.05);
    w.vfx?.spark?.(e.x[id] + dirX * -0.5, e.y[id] + 1.2, e.z[id] + dirZ * -0.5, -dirX, 0.6, -dirZ, 0.7);
    if (frac01(rec.spawnKey, w.time.tick & 255) < 0.62) {
      setOf(w, rec, id, OF.COUNTER, 0.46);
      e.state[id] = ST.WINDUP;
      play(w, id, 'attack_light_2', 0.06);
      w.ui?.toast?.(`${rec.name} parries!`);
    } else {
      rec.dur = Math.max(rec.dur, rec.ofT + 0.35);
    }
    return true;
  }

  // ---- armour ----------------------------------------------------------
  const armored = e.has(id, FLAG.SUPERARMOR) || (rec.phase >= 3 && heavy < 2);
  if (armored) {
    e.flash[id] = 1;
    return true;                                   // shrugs it off, keeps going
  }

  // ---- composure -------------------------------------------------------
  // A launcher always launches: the moveset must keep one reliable answer to a
  // general who is otherwise shrugging blows off.
  const breaks = heavy >= 2 || (rec.poise -= heavy >= 1 ? POISE_HEAVY : POISE_LIGHT) <= 0;

  if (!breaks) {
    // He wears it. The blow still reads — flash, sound, and a shove that moves
    // him without taking the fight off him — but he keeps whatever he was doing,
    // which means the player standing in front of him is about to be hit.
    e.flash[id] = 1;
    e.vx[id] += dirX * 0.45;
    e.vz[id] += dirZ * 0.45;
    play(w, id, 'hit_light', 0.05);
    return true;
  }

  // ---- interrupted -----------------------------------------------------
  rec.poise = rec.poiseMax;
  setOf(w, rec, id, OF.HURT, heavy >= 2 ? 0.62 : 0.5);
  e.state[id] = ST.HIT;
  e.stagger[id] = heavy >= 1 ? 0.7 : 0.45;
  e.vx[id] += dirX * (heavy >= 2 ? 3.4 : 1.6);
  e.vz[id] += dirZ * (heavy >= 2 ? 3.4 : 1.6);
  play(w, id, heavy >= 1 ? 'hit_heavy' : 'hit_light', 0.05);
  return true;
}

export { NAMES_KIDUL, NAMES_WILWATIKTA, TITLES };
